import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    MdChevronRight as MdChevron,
    MdClose,
    MdDownload,
    MdInsertDriveFile,
    MdOpenInNew,
    MdPictureAsPdf,
    MdTableChart,
} from 'react-icons/md';
import ExcelPreview from '../ExcelPreview';
import {
    DocumentItem,
    formatDisplayDate,
    formatTime,
    isExcel,
    isPdf,
    ScheduleEntry,
} from './schedule.types';

const FileIcon = ({ fileType, size = 18 }: { fileType: string; size?: number }) => {
    if (isPdf(fileType)) return <MdPictureAsPdf size={size} className="text-red-500" />;
    if (isExcel(fileType)) return <MdTableChart size={size} className="text-green-600" />;
    return <MdInsertDriveFile size={size} className="text-slate-400" />;
};

const FileTypeBadge = ({ fileType }: { fileType: string }) => {
    if (isPdf(fileType))
        return (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-500 border border-red-100">
                PDF
            </span>
        );
    if (isExcel(fileType))
        return (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-600 border border-green-100">
                Excel
            </span>
        );
    return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-400 border border-slate-100">
            File
        </span>
    );
};

interface CoordinatorSidebarProps {
    open: boolean;
    selectedDate: string | null;
    coordinators: ScheduleEntry[];
    selectedCoordinator: ScheduleEntry | null;
    selectedDoc: DocumentItem | null;
    previewLoaded: boolean;
    setPreviewLoaded: (v: boolean) => void;
    setSelectedDoc: (doc: DocumentItem | null) => void;
    onCoordinatorClick: (entry: ScheduleEntry) => void;
    onClose: () => void;
    onDownload: (doc: DocumentItem) => void;
}

const CoordinatorSidebar: React.FC<CoordinatorSidebarProps> = ({
    open,
    selectedDate,
    coordinators,
    selectedCoordinator,
    selectedDoc,
    previewLoaded,
    setPreviewLoaded,
    setSelectedDoc,
    onCoordinatorClick,
    onClose,
    onDownload,
}) => {
    // Lock body scroll while open
    useEffect(() => {
        if (!open) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    return createPortal(
        <div
            className={`fixed inset-0 ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
            style={{ zIndex: 2147483600 }}
            aria-modal="true"
            role="dialog"
            aria-hidden={!open}
        >
            {/* Backdrop — plain bg-black/50, no backdrop-blur for mobile perf */}
            <div
                onClick={onClose}
                className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ease-out ${open ? 'opacity-100' : 'opacity-0'
                    }`}
            />

            {/* Drawer — always mounted, only transform changes */}
            <div
                className={`absolute top-0 right-0 bottom-0 flex flex-col w-full max-w-sm sm:max-w-md bg-white shadow-2xl transition-transform duration-300 ease-out will-change-transform ${open ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
                    <div>
                        <h3 className="font-semibold text-slate-800 text-base">Coordinators</h3>
                        {selectedDate && (
                            <p className="text-xs text-slate-400 mt-0.5">
                                {formatDisplayDate(selectedDate)}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                        aria-label="Close sidebar"
                    >
                        <MdClose size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {coordinators.map((entry) => {
                        const isSelected = selectedCoordinator?.id === entry.id;
                        const docs = entry.coordinator.documents;

                        return (
                            <div
                                key={entry.id}
                                className="rounded-2xl border border-slate-100 overflow-hidden bg-white shadow-sm"
                            >
                                <button
                                    onClick={() => onCoordinatorClick(entry)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isSelected ? 'bg-brand-50' : 'hover:bg-slate-50'
                                        }`}
                                >
                                    <img
                                        src={entry.coordinator.avatar_url}
                                        alt={entry.coordinator_name}
                                        className="w-10 h-10 rounded-full object-cover shrink-0 border border-slate-100"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                                entry.coordinator_name
                                            )}&background=e0e7ff&color=4f46e5&size=80`;
                                        }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm text-slate-800 truncate flex items-center gap-2">
                                            {entry.coordinator_name}
                                            {entry.service_name && (
                                                <span
                                                    className="text-[10px] text-slate-400 truncate"
                                                >
                                                    {entry.service_name}
                                                </span>
                                            )}
                                        </p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            {entry.start_time && (
                                                <span className="text-xs text-slate-400">
                                                    {formatTime(entry.start_time)} – {formatTime(entry.end_time)}
                                                </span>
                                            )}
                                            {docs.length > 0 && (
                                                <span className="text-[11px] font-semibold text-brand-700 bg-brand-50 border border-brand-100 px-1.5 py-0.5 rounded-md">
                                                    {docs.length} doc{docs.length > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <MdChevron
                                        size={18}
                                        className={`shrink-0 transition-transform duration-200 ${isSelected ? 'rotate-90 text-brand-600' : 'text-slate-300'
                                            }`}
                                    />
                                </button>

                                {isSelected && (
                                    <div className="border-t border-slate-100">
                                        {docs.length === 0 ? (
                                            <p className="text-xs text-slate-400 text-center py-6">
                                                No documents for this date
                                            </p>
                                        ) : (
                                            <>
                                                <div className="divide-y divide-slate-50">
                                                    {docs.map((doc) => (
                                                        <div
                                                            key={doc.id}
                                                            onClick={() =>
                                                                setSelectedDoc(
                                                                    selectedDoc?.id === doc.id ? null : doc
                                                                )
                                                            }
                                                            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${selectedDoc?.id === doc.id
                                                                ? 'bg-brand-50'
                                                                : 'hover:bg-slate-50'
                                                                }`}
                                                        >
                                                            <div
                                                                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isPdf(doc.file_type)
                                                                    ? 'bg-red-50'
                                                                    : isExcel(doc.file_type)
                                                                        ? 'bg-green-50'
                                                                        : 'bg-slate-50'
                                                                    }`}
                                                            >
                                                                <FileIcon fileType={doc.file_type} size={16} />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-semibold text-slate-700 truncate">
                                                                    {doc.title}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onDownload(doc);
                                                                }}
                                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-brand-600 hover:text-white transition-all shrink-0"
                                                                title="Download"
                                                            >
                                                                <MdDownload size={14} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>

                                                {selectedDoc && docs.some((d) => d.id === selectedDoc.id) && (
                                                    <div className="border-t border-slate-100">
                                                        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50">
                                                            <div className="flex items-center gap-1.5 min-w-0">
                                                                <FileIcon
                                                                    fileType={selectedDoc.file_type}
                                                                    size={13}
                                                                />
                                                                <span className="text-[11px] font-semibold text-slate-700 truncate max-w-[140px]">
                                                                    {selectedDoc.title}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-1 shrink-0">
                                                                <button
                                                                    onClick={() =>
                                                                        window.open(
                                                                            selectedDoc.file_url,
                                                                            '_blank',
                                                                            'noopener,noreferrer'
                                                                        )
                                                                    }
                                                                    className="flex items-center gap-1 px-2 py-1 text-slate-600 text-[11px] font-medium rounded-lg border border-slate-200 hover:bg-white transition-colors"
                                                                >
                                                                    <MdOpenInNew size={12} />
                                                                    Open
                                                                </button>
                                                                <button
                                                                    onClick={() => onDownload(selectedDoc)}
                                                                    className="flex items-center gap-1 px-2 py-1 bg-brand-700 text-white text-[11px] font-medium rounded-lg hover:bg-brand-800 transition-colors"
                                                                >
                                                                    <MdDownload size={12} />
                                                                    Download
                                                                </button>
                                                                <button
                                                                    onClick={() => setSelectedDoc(null)}
                                                                    className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                                                                >
                                                                    <MdClose size={13} />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {isPdf(selectedDoc.file_type) ? (
                                                            <div
                                                                className="relative bg-slate-100"
                                                                style={{ height: 380 }}
                                                            >
                                                                {!previewLoaded && (
                                                                    <div className="absolute inset-0 z-10 p-3">
                                                                        <div className="h-full w-full rounded-xl bg-white border border-slate-100 animate-pulse" />
                                                                    </div>
                                                                )}
                                                                <iframe
                                                                    src={`${selectedDoc.file_url}#toolbar=0&navpanes=0&scrollbar=0`}
                                                                    className="w-full h-full"
                                                                    title={selectedDoc.title}
                                                                    onLoad={() => setPreviewLoaded(true)}
                                                                />
                                                            </div>
                                                        ) : isExcel(selectedDoc.file_type) ? (
                                                            <ExcelPreview
                                                                url={selectedDoc.file_url}
                                                                title={selectedDoc.title}
                                                                height={380}
                                                            />
                                                        ) : (
                                                            <div className="flex items-center justify-center py-8 text-slate-400 text-xs bg-slate-50">
                                                                No preview available
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CoordinatorSidebar;